import { ExternalLinkIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { SpinnerIcon } from '@/components/ui/spinner';
import { useDeskId } from '@/hooks/useDeskId';
import { ContactLookupResult } from '@/modules/humanChat/hooks/useContactLookup';

interface ContactLookupHintProps {
  result: ContactLookupResult | null;
  loading: boolean;
  queried: boolean;
}

export const ContactLookupHint = ({ result, loading, queried }: ContactLookupHintProps) => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const deskId = useDeskId();

  if (loading) {
    return (
      <p className="flex items-center gap-1 text-sm text-muted-foreground">
        <SpinnerIcon className="size-4 text-foreground" />
        {t('conversation.new_message.contact_lookup.checking')}
      </p>
    );
  }

  if (!queried) {
    return null;
  }

  if (!result) {
    return (
      <p className="text-sm text-muted-foreground">
        {t('conversation.new_message.contact_lookup.new_contact')}
      </p>
    );
  }

  const conversationUrl = `/${slug}/chats/${deskId}/conversations/${result.lastSessionId}`;

  return (
    <p className="text-sm text-muted-foreground">
      {result.userName ? (
        <>
          <span className="font-medium text-foreground">{result.userName}</span>
          {' — '}
          {t('conversation.new_message.contact_lookup.found', { 0: result.sessionCount })}
        </>
      ) : (
        t('conversation.new_message.contact_lookup.found', { 0: result.sessionCount })
      )}{' '}
      <a
        href={conversationUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
      >
        {t('conversation.new_message.contact_lookup.view_last')}
        <ExternalLinkIcon className="h-3 w-3" />
      </a>
    </p>
  );
};
